import { useEffect, useState } from 'react';
import axios from 'axios';
import utils from '../../utils/utils';
import MODULE_SUB_MODULE_OPERATIONS_TREE from '../../const/ModuleSubModuleOperationsTree';
import useConsole from '../Console/useConsole';
import useDatasetGrid from '../useDatasetGrid';
import ApiEndpoints from '../../const/ApiEndpoints';
import useProject from '../Project/useProject';
import useDataIngestion from '../DM/useDataIngestion';
import useModelMetaData from '../useModelMetaData';
import { DATASET_REQUIRED_FIELDS } from '../../const/CommonConst';

const SAMPLING_BASE_URL = ApiEndpoints.MODULE_BASE_URL.SAMPLING;
const totalPageCount = 'x-total-count';

/**
 * @function useSampling
 * @description hook for sampling-sub module of sampling&segmentation module
 * @param {function} fetchModuleTasks - function for fetching module tasks
 * @param {array} datasetList - list of datasets
 * @param {function} fetchDatasetList - function for fetching dataset list
 * @param {function} fetchDatasetRowsWithPagination - function for fetching dataset rows with pagination
 * @param {array} datasetRowsWithPagination - list of dataset rows with pagination
 * @param {function} setDatasetRowsWithPagination - function for setting dataset rows with pagination
 * @param {number} totalDatasetRows - total number of dataset rows
 * @param {number} datasetRowsPageCount - dataset rows page count
 * @param {boolean} isLoading - boolean for showing loader
 * @param {function} updateDatasetCells - function for updating dataset cells
 * @param {object} project - project object
 * @returns {object} -returning multiple methods and states for
 * sampling-sub module of sampling&segmentation module
 * @example const {fetchModuleTasks,fetchDatasetList,datasetList}=useSampling()
 */

export default function useSampling() {
  const {
    fetchModuleTasks,

    fetchDatasetList,
    datasetList,

    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,

    updateDatasetCells,
    project,
  } = useProject();

  const {
    renameColumn,
    deleteColumn,
    duplicateColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    tasks: gridTasks,
    taskCount: gridTaskCount,
    fetchTasks: fetchGridTasks,
    terminateTask: terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
    gridOperationRerun,
  } = useDatasetGrid();
  const {
    consoleModuleTasks,
    setConsoleModuleTasks,
    consoleAsyncTaskResults,
    fetchAsyncResultConsole,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    setConsoleAsyncTaskResults,
    fetchAllModels,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    fetchConsoleFileList,
  } = useConsole();
  const { markDatasetAsReadyToUse } = useDataIngestion();

  const { addResultToModelMetaData, fetchModelMetaDataSchema } =
    useModelMetaData();

  const [activeOperation, setActiveOperation] = useState({});

  const [activeDataset, setActiveDataset] = useState('');

  const [datasetColumnDetails, setDatasetColumnDetails] = useState([]);

  const [moduleTasks, setModuleTasks] = useState([]);
  const [totalModuleTask, setTotalModuleTask] = useState(0);
  const [totalModuleTaskConsole, setTotalModuleTaskConsole] = useState(0);
  const [taskCount, setTaskCount] = useState({});

  const [taskDetails, updateTaskDetails] = useState({});

  const resetTaskDetails = () => {
    updateTaskDetails({});
  };

  useEffect(() => {
    if (activeDataset?.id) {
      datasetList.forEach((data) => {
        if (data.id === activeDataset.id) {
          setActiveDataset(data);
        }
      });
    }
  }, [datasetList]);

  /**
   * @function fetchDatasetColumnDetails
   * @description API call for fetching dataset column details
   * @param {string} datasetName - name of the dataset
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns {Promise} AxiosPromise
   * @example fetchDatasetColumnDetails(datasetName,successCallback);
   */
  const fetchDatasetColumnDetails = (datasetName, successCallback = () => {}) =>
    axios
      .post(
        `${SAMPLING_BASE_URL + ApiEndpoints.SAMPLING.DATASET_COLUMN_DETAILS}`,
        datasetName
      )
      .then(
        (res) => {
          setDatasetColumnDetails(res.data?.columns);
          successCallback(res);
        },
        () => {}
      );
  /**
   * @function fetchSModuleTasks
   * @description API call for fetching sampling module tasks
   * @param {number} projectId - project id
   * @param {boolean} hideLoaderOnRequest - param for hiding loader in UI while calling an api.
   * @returns {undefined}
   * @example fetchSModuleTasks(project_id,hideLoaderOnRequest);
   */
  const fetchSModuleTasks = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      return fetchModuleTasks(
        'SAMPLING',
        MODULE_SUB_MODULE_OPERATIONS_TREE.SS.subModules.SA.key,
        'GET_SAMPLING_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        pageIndex,
        pageSize,
        taskStatus
      ).then((res) => {
        if (res.data) {
          setTotalModuleTask(res.headers?.[totalPageCount]);
          if (pageIndex > 1) {
            setModuleTasks((prevModuleTasks) => [
              ...prevModuleTasks,
              ...(res?.data?.tasks || []),
            ]);
          } else {
            setModuleTasks([...res.data.tasks]);
          }
        }
        return res;
      });
    }
  };

  /**
   * Fetches the count of module tasks.
   *
   * @param {string} projectId - The ID of the project.
   * @param {boolean} hideLoaderOnRequest - Whether to hide the loader on request.
   * @param {number} count - The count of tasks to fetch.
   * @return {Promise} A promise that resolves with the count of tasks.
   */
  const fetchModuleTasksCount = (projectId, hideLoaderOnRequest, count) => {
    if (projectId) {
      fetchModuleTasks(
        'SAMPLING',
        MODULE_SUB_MODULE_OPERATIONS_TREE.SS.subModules.SA.key,
        'GET_SAMPLING_TASKS',
        () => {},
        projectId,
        hideLoaderOnRequest,
        '',
        null,
        null,
        null,
        count
      ).then((res) => {
        setTaskCount(res?.data);
      });
    }
  };

  const fetchAsyncResultSplit = (taskID, type) =>
    axios
      .get(
        `${SAMPLING_BASE_URL}${ApiEndpoints.SAMPLING.GET_SAMPLING_TASKS}/${taskID}/${type}/`
      )
      .then((res) => {
        return res;
      });

  /**
   * @function fetchSModuleTasksConsole
   * @description API call for fetching sampling module tasks
   * @param {number} project_id - project id
   * @param {boolean} hideLoaderOnRequest - param for hiding loader in UI while calling an api.
   * @returns {undefined}
   * @example fetchSModuleTasksConsole(project_id,hideLoaderOnRequest);
   */
  const fetchSModuleTasksConsole = (
    projectId,
    hideLoaderOnRequest,
    pageIndex,
    pageSize,
    taskStatus
  ) => {
    if (projectId) {
      fetchModuleTasksConsole(
        MODULE_SUB_MODULE_OPERATIONS_TREE.SS.objKey,
        MODULE_SUB_MODULE_OPERATIONS_TREE.SS.subModules.SA.key,
        'GET_SAMPLING_TASKS',
        projectId,
        hideLoaderOnRequest,
        pageIndex,
        pageSize,
        taskStatus
      ).then((res) => {
        if (res.data) {
          setTotalModuleTaskConsole(res.headers[totalPageCount]);
          if (pageIndex > 1) {
            setConsoleModuleTasks([...consoleModuleTasks, ...res.data]);
          } else {
            setConsoleModuleTasks([...res.data]);
          }
        }
      });
    }
  };
  /**
   * @function fetchAsyncResult
   * @description API call for fetching asynchronous result
   * @param {string} taskID - task id
   * @param {object} additionTaskDetails - addition task details
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns {Promise} AxiosPromise
   * @example fetchAsyncResult(taskID, additionTaskDetails, successCallback);
   */
  const fetchAsyncResult = (taskID, additionTaskDetails, successCallback) =>
    axios
      .get(
        `${`${
          SAMPLING_BASE_URL + ApiEndpoints.SAMPLING.GET_SAMPLING_TASKS
        }/${taskID}`}`
      )
      .then(
        (res) => {
          if (res.data.task_status === 'SUCCESS') {
            Object.values(
              MODULE_SUB_MODULE_OPERATIONS_TREE.SS.subModules.SA.operations
            ).filter((data) => {
              if (
                data.key === additionTaskDetails.operation &&
                res.data?.parameters?.dataset_id
              ) {
                datasetList.forEach((dataset) => {
                  if (dataset.id === Number(res.data.parameters.dataset_id)) {
                    setActiveDataset(dataset);
                  }
                });

                updateTaskDetails(res.data);
                setActiveOperation(data);
              }
              return null;
            });
            successCallback(res.data);
          } else {
            updateTaskDetails({});
          }
        },
        () => {}
      );

  /**
   * @function fetchAsyncResultConsoleAndActivateDataset
   * @description API call for fetching asynchronous result
   * @param {number}  taskID - task id
   * @param {object}  additionTaskDetails - addition task details
   * @returns  {Promise} AxiosPromise
   * @example  fetchAsyncResultConsoleAndActivateDataset(taskID, additionTaskDetails)
   */
  const fetchAsyncResultConsoleAndActivateDataset = (
    taskID,
    additionTaskDetails
  ) =>
    fetchAsyncResultConsole(taskID, additionTaskDetails).then(
      (res) => {
        if (res.data.task_status === 'SUCCESS') {
          if (utils.isNewDatasetCreatedByUdf(res.data)) {
            fetchDatasetList(
              project?.activeProject?.project_id,
              true,
              [DATASET_REQUIRED_FIELDS].join()
            );
          }
          if (res.data?.parameters?.table) {
            datasetList.forEach((dataset) => {
              if (dataset.Name === res.data.parameters.table) {
                setActiveDataset({ ...dataset });
              }
            });
          }
        }
        return res;
      },
      (error) => {
        throw error;
      }
    );

  /**
   * @function terminateTask
   * @description API call for terminating task
   * @param {string} taskID - task id
  
   * @returns {Promise} AxiosPromise
   * @example terminateTask(taskID);
   */
  const terminateTask = (taskID) =>
    axios
      .delete(
        `${`${
          SAMPLING_BASE_URL + ApiEndpoints.SAMPLING.GET_SAMPLING_TASKS
        }/${taskID}`}`
      )
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });

  /**
   * @function samplingRerunOperation
   * @description API call for reruning the operation
   * @param {object} body - body
   * @returns {Promise} AxiosPromise
   * @example samplingRerunOperation(body);
   */
  const samplingRerunOperation = (body) => {
    return axios
      .post(`${SAMPLING_BASE_URL + ApiEndpoints.SAMPLING.RERUN}`, body)
      .then((res) => {
        return res;
      })
      .catch((e) => {
        throw e;
      });
  };

  /**
   * @function visualizeImbalance
   * @description API call to get visualize imbalance operation result
   * @param {object} body -data for visualize imbalance operation result
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns {Promise} AxiosPromise
   * @example visualizeImbalance(body, successCallback);
   */
  const visualizeImbalance = (body, successCallback) =>
    axios
      .post(
        `${
          SAMPLING_BASE_URL + ApiEndpoints.SAMPLING.SAMPLING_VISUALIZE_IMBALANCE
        }`,
        body
      )
      .then(
        () => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          successCallback();
        },
        (error) => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          throw error;
        }
      );
  /**
   * @function resamplingResults
   * @description API call to get resampling operation result
   * @param {object} body -data for resampling operation result
   * @param {function} successCallback - Callback function to execute upon completion of API call
  
   * @returns {Promise} AxiosPromise
   * @example resamplingResults(body, successCallback);
   */
  const resamplingResults = (body, successCallback) =>
    axios
      .post(
        `${SAMPLING_BASE_URL + ApiEndpoints.SAMPLING.SAMPLING_RESAMPLING}`,
        body
      )
      .then(
        () => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          successCallback();
        },
        (error) => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          throw error;
        }
      );
  /**
   * @function fetchResamplingMethods
   * @description API call to get methods of resampling operation
   * @param {string} table_name -name of the dataset
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @param {function} errorCallback - Callback function to execute
   * when any error happens during the calling of API
   * @returns {Promise} AxiosPromise
   * @example fetchResamplingMethods(table_name, successCallback, errorCallback);
   */
  const fetchResamplingMethods = (tableName, successCallback, errorCallback) =>
    axios
      .get(
        `${
          SAMPLING_BASE_URL + ApiEndpoints.SAMPLING.SAMPLING_RESAMPLING
        }?methodology=${tableName}`
      )
      .then(
        (res) => {
          successCallback(res);
        },
        (error) => {
          errorCallback(error);
        }
      );
  /**
   * @function samplingResults
   * @description API call to get sampling operation result
   * @param {object} body -data for sampling operation result
   * @param {function} successCallback - Callback function to execute upon completion of API call
 
   * @returns {Promise} AxiosPromise
   * @example samplingResults(body, successCallback);
   */
  const samplingResults = (body, successCallback) =>
    axios
      .post(
        `${SAMPLING_BASE_URL + ApiEndpoints.SAMPLING.SAMPLING_SAMPLING}`,
        body
      )
      .then(
        () => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          successCallback();
        },
        (error) => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          throw error;
        }
      );
  /**
   * @function timeSeriesAnalysisResults
   * @description API call to get time series analysis operation of sampling sub module result
   * @param {object} body -data for time series analysis operation of sampling sub module result
   * @param {function} successCallback - Callback function to execute upon completion of API call

   * @returns {Promise} AxiosPromise
   * @example timeSeriesAnalysisResults(body, successCallback);
   */
  const timeSeriesAnalysisResults = (body, successCallback) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.SAMPLING +
          ApiEndpoints.SAMPLING.SAMPLING_TSA
        }`,
        body
      )
      .then(
        () => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          successCallback();
        },
        (error) => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          throw error;
        }
      );
  /**
   * @function splittingResults
   * @description API call to get splitting operation result
   * @param {object} body -data for splitting operation result
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @returns {Promise} AxiosPromise
   * @example splittingResults(body, successCallback);
   */
  const splittingResults = (body, successCallback) =>
    axios
      .post(
        `${SAMPLING_BASE_URL + ApiEndpoints.SAMPLING.SAMPLING_SPLITTING}`,
        body
      )
      .then(
        () => {
          if (taskDetails?.results) {
            updateTaskDetails({
              ...taskDetails,
              results: {},
            });
          }
          successCallback();
        },
        (error) => {
          throw error;
        }
      );
  /**
   * @function markTaskReadUnreadSampling
   * @description API call for mark task read unread of sampling sub module
   * @param {object} taskParams -task parameters of mark task read unread of sampling sub module
   * @param {function} successCallback - Callback function to execute upon completion of API call

   * @returns {Promise} AxiosPromise
   * @example markTaskReadUnreadSampling(taskParams,successCallback);
   */
  const markTaskReadUnreadSampling = (taskParams, successCallback) =>
    axios
      .get(
        `${
          SAMPLING_BASE_URL +
          ApiEndpoints.SAMPLING.SAMPLING_JOB_QUEUE_MARK_READ_UNREAD
        }`,
        {
          params: taskParams,
        }
      )
      .then(
        () => {
          successCallback();
        },
        () => {}
      );

  return {
    activeOperation,
    setActiveOperation,

    activeDataset,
    setActiveDataset,

    datasetColumnDetails,
    fetchDatasetColumnDetails,

    moduleTasks,
    setModuleTasks,
    totalModuleTask,
    fetchSModuleTasks,
    taskCount,
    fetchModuleTasksCount,

    taskDetails,
    resetTaskDetails,
    fetchAsyncResult,

    visualizeImbalance,
    resamplingResults,
    samplingResults,
    splittingResults,
    fetchResamplingMethods,
    fetchDatasetList,
    datasetList,
    terminateTask,

    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,

    updateDatasetCells,
    project,
    renameColumn,
    samplingRerunOperation,
    duplicateColumn,
    deleteColumn,
    addColumn,
    deleteRow,
    duplicateRow,
    addRow,
    gridTasks,
    gridTaskCount,
    fetchGridTasks,
    terminateGridTask,
    manageSpaces,
    manageCases,
    splitColumn,
    findAndReplace,
    sortColumn,
    analyse,
    embeddedValues,
    standardisationError,
    integrityRules,
    uniqueness,
    scores,
    fetchGridTaskData,
    markTaskReadUnreadSampling,
    timeSeriesAnalysisResults,
    findDuplicateRows,
    findDuplicateColumns,
    findAndDeleteDuplicateRows,
    findAndDeleteDuplicateColumns,
    setGridTaskResultsData,
    gridTaskResultsData,
    resetGridTaskResultsData,
    markTaskReadUnreadGrid,
    valueError,
    findBlankValuePercentage,
    replaceBlankValues,
    findRegexPercentage,
    gridOperationRerun,
    markDatasetAsReadyToUse,

    // console related
    consoleModuleTasks,
    totalModuleTaskConsole,
    consoleAsyncTaskResults,
    fetchAsyncResultConsoleAndActivateDataset,
    terminateTaskConsole,
    markTaskReadUnreadConsole,
    fetchSModuleTasksConsole,
    fetchUdfs,
    deleteUdf,
    createUdf,
    createJob,
    updateUdf,
    udfs,
    selectedUdf,
    setSelectedUdf,
    runUdf,
    githubIntegration,
    fetchUdfDetails,
    fetchDatasetColumnDetailsForConsole,
    fetchAllModels,
    setConsoleAsyncTaskResults,
    fetchTableData,
    onDownloadCSV,
    fetchConsoleLibraries,
    consoleRerunOperation,
    fetchConsoleFileList,
    fetchAsyncResultSplit,
    addResultToModelMetaData,
    fetchModelMetaDataSchema,
  };
}
